import { pushPageView } from "@features/analytics/pushPageView"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"

// GTM/Consent globals
declare global {
  interface Window {
    dataLayer?: any[]
    gtag?: (...args: any[]) => void
  }
}

const CookiePopup = () => {
  const [showPopup, setShowPopup] = useState(false)
  const [, setConsent] = useState<boolean | null>(null)
  const { t } = useTranslation("common/cookiePopup")

  // --- Constants ---
  const COOKIE_FLAG = "cookie_consent" // boolean-only (coarse) for this site
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

  // Safe gtag shim (writes to dataLayer)
  function ensureGtag() {
    window.dataLayer = window.dataLayer || []
    if (!window.gtag) {
      window.gtag = (...args: any[]) => window.dataLayer!.push(args)
    }
  }

  // Reflect a boolean decision into Consent Mode v2
  function applyConsent(granted: boolean) {
    ensureGtag()
    window.gtag!("consent", "update", {
      ad_storage: granted ? "granted" : "denied",
      analytics_storage: granted ? "granted" : "denied",
      ad_user_data: granted ? "granted" : "denied",
      ad_personalization: granted ? "granted" : "denied",
    })
    // 🔔 make GTM re-evaluate tags under the new consent
    window.dataLayer!.push({ event: "gtm.consentUpdate" })
  }

  useEffect(() => {
    const saved = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${COOKIE_FLAG}=`))
      ?.split("=")[1]

    if (saved === "true") {
      setConsent(true)
      applyConsent(true)
      // ✅ count the landing page (first load was under "denied")
      pushPageView()
    } else if (saved === "false") {
      setConsent(false)
      applyConsent(false)
    } else {
      setShowPopup(true) // no saved choice: show banner
    }
  }, [])

  const acceptCookies = () => {
    document.cookie = `${COOKIE_FLAG}=true; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`
    setConsent(true)
    applyConsent(true)
    // ✅ send one page_view immediately after granting
    pushPageView()
    setShowPopup(false)
  }

  const declineCookies = () => {
    document.cookie = `${COOKIE_FLAG}=false; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`
    setConsent(false)
    applyConsent(false)
    setShowPopup(false)
  }

  if (!showPopup) return null

  return (
    <PopupContainer>
      <Popup>
        <PopupText>
          {t(
            "description",
            "To provide the best experiences, we use technologies like cookies to store and/or access device information. Consenting to these technologies will allow us to process data such as browsing behavior or unique IDs on this site.",
          )}
        </PopupText>
        <ButtonGroup>
          <AcceptButton onClick={acceptCookies}>{t("accept", "Accept")}</AcceptButton>
          <DeclineButton onClick={declineCookies}>{t("decline", "Decline")}</DeclineButton>
        </ButtonGroup>
      </Popup>
    </PopupContainer>
  )
}

export default CookiePopup

// ---------------------- STYLES ----------------------
const PopupContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 30%;
  max-width: 750px;
  background: ${({ theme }) => theme.white}35;
  backdrop-filter: blur(5px);
  color: ${({ theme }) => theme.tertiary};
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.5s ease-in-out;
  z-index: 999;

  @media (max-width: 768px) {
    width: auto;
    left: 3%;
    right: 3%;
    transform: unset;
  }
`
const Popup = styled.div``
const PopupText = styled.p`
  font-size: 14px;
  margin-bottom: 12px;
  color: white;
`
const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`
const AcceptButton = styled.button`
  background: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.primary};
  padding: 10px 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  &:hover {
    background: ${({ theme }) => theme.primary}90;
    color: white;
  }
`
const DeclineButton = styled.button`
  background: ${({ theme }) => theme.white}80;
  color: ${({ theme }) => theme.primary};
  padding: 10px 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background: ${({ theme }) => theme.primary}70;
    color: white;
  }
`
